import { useCallback, useMemo } from 'react';
import {
  AllButLast,
  DecorateAcknowledgements,
  FirstArg,
  Last,
} from 'socket.io-client/build/esm/socket';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { RobotConnectionStatus, RobotDevice } from '../../types';
import { AppToRobotEvents, AppToRobotRequests } from '../commands';

type Props = {
  robotId: string;
};

/**
 * This hook listens to a particular robot socketio connection and exposes functionality relating to disconnecting from it,
 * as well as sending events and requests to it.
 *
 * @param props.robotId --> ID of the robot connection to listen to
 * @returns
 * - isConnected: Status of robot connection
 * - disconnect: Method to disconnect the robot connection
 * - sendEvent: Method to send an event, with no value returned
 * - sendRequest: Method to send a request, returning a promise with the responses
 */
export const useRobotConnection = (props: Props) => {
  const { robotId } = props;

  const robotDevice: RobotDevice = useRobotStateStore(
    (store) => store.robotDevices[robotId]
  );

  /**
   * what is the status of this connection
   */
  const connectionStatus: RobotConnectionStatus = useMemo(() => {
    if (robotDevice?.websocketConnection?.connectionStatus === 'connected') {
      return 'connected';
    } else if (
      robotDevice?.bluetoothConnection?.connectionStatus === 'connected' ||
      robotDevice?.bluetoothConnection?.connectionStatus === 'connecting' ||
      robotDevice?.websocketConnection?.connectionStatus === 'connecting'
    ) {
      return 'connecting';
    } else if (
      robotDevice?.bluetoothConnection?.connectionStatus === 'disconnecting'
    ) {
      return 'disconnecting';
    } else {
      return 'disconnected';
    }
  }, [
    robotDevice?.bluetoothConnection?.connectionStatus,
    robotDevice?.websocketConnection?.connectionStatus,
  ]);

  /**
   * Disconnect from the robot connections
   */
  const disconnect = useCallback(() => {
    robotDevice?.websocketConnection?.socket?.disconnect?.();
  }, [robotDevice?.websocketConnection?.socket]);

  /**
   * Send an event to the robot connection.
   *
   * @param event Name of event, use intellisense to see options
   * @param args Rest of arguments for event, use intellisense to see options
   */
  const sendEvent = useCallback(
    function <T extends keyof AppToRobotEvents>(
      event: T,
      ...args: Parameters<AppToRobotEvents[T]>
    ) {
      robotDevice?.websocketConnection?.socket?.emit(
        event,
        ...(args ?? ([] as any))
      );
    },
    [robotDevice?.websocketConnection?.socket]
  );

  /**
   * Send a request to the robot connection.
   *
   * @param timeout How long to wait before timing out the promise. Required
   * @param event Name of request, use intellisense to see options.
   * @param args Rest of arguments for request, use intellisense to see options
   *
   * @returns Promise containing response from robot.
   * @throws An error if timeout occurs
   */
  const sendRequest = useCallback(
    async function <T extends keyof AppToRobotRequests>(
      timeout: number,
      event: T,
      ...args: AllButLast<
        Parameters<DecorateAcknowledgements<AppToRobotRequests>[T]>
      >
    ): Promise<FirstArg<Last<Parameters<AppToRobotRequests[T]>>> | undefined> {
      const socket = robotDevice?.websocketConnection?.socket?.timeout(timeout);
      const res = await socket?.emitWithAck(event, ...(args ?? ([] as any)));

      return res;
    },
    [robotDevice?.websocketConnection?.socket]
  );

  return {
    robotDevice,
    disconnect,
    sendEvent,
    sendRequest,
    connectionStatus,
  };
};
